import { useField } from 'formik';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import useClassNames, { IClassNames } from '../../hooks/classNames.hooks';

interface SwitchItem {
  value: string | number | boolean;
  label: string;
}

interface SwitchProps extends IClassNames {
  name: string;
  label?: string;
  off: SwitchItem;
  on: SwitchItem;
  ariaLabel?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  description?: string;
}

const Switch: React.FC<SwitchProps> = ({
  name,
  label,
  off,
  on,
  ariaLabel,
  onChange,
  description,
  classNames,
}) => {
  const [{ value = off.value }, { error }, { setValue }] = useField(name);
  const htmlId = React.useMemo(() => uuidv4(), []);
  const { className } = useClassNames('a-input', error && 'has-error', classNames);
  const isChecked = React.useMemo(() => value === on.value, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.checked ? on.value : off.value);

    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className={className}>
      {label && <label className="a-input__label">{label}</label>}
      <div className="a-switch">
        <label className={`a-switch__label ${!isChecked && 'u-text-bold'}`}>{off.label}</label>
        <div className="a-switch__toggle">
          <input
            type="checkbox"
            name={name}
            id={htmlId}
            role="switch"
            onChange={handleChange}
            aria-checked={isChecked}
            checked={isChecked}
            aria-describedby={description && `${htmlId}-description`}
          />
          <label htmlFor={htmlId}>
            <span className="u-screen-reader-only">{ariaLabel}</span>
          </label>
        </div>
        <label className={`a-switch__label ${isChecked && 'u-text-bold'}`}>{on.label}</label>
      </div>

      {description && <small id={`${htmlId}-description`}>{description}</small>}
    </div>
  );
};

export default Switch;
